import { useState } from "react";
import jsPDF from "jspdf";
import { Notebook, Download } from "@/lib/icons";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  {
    title: "1. Title & Date",
    prompt: "Experiment title, your name, partner(s), date, class period.",
    lines: 3,
  },
  {
    title: "2. Question / Purpose",
    prompt: "What are you trying to find out? Phrase as a testable question.",
    lines: 3,
  },
  {
    title: "3. Hypothesis",
    prompt:
      "If ___ then ___ because ___ . Identify your independent and dependent variables.",
    lines: 4,
  },
  {
    title: "4. Materials",
    prompt: "Bullet list every chemical, glassware, and instrument with sizes/concentrations.",
    lines: 6,
  },
  {
    title: "5. Safety (PPE & SDS notes)",
    prompt:
      "PPE worn, hazards reviewed (SDS reference), spill / first-aid plan, waste disposal.",
    lines: 5,
  },
  {
    title: "6. Procedure",
    prompt: "Numbered steps in past tense. Anyone should be able to repeat your work.",
    lines: 10,
  },
  {
    title: "7. Data & Observations",
    prompt:
      "Tables with units and sig figs. Qualitative observations (color, smell, gas, heat) on the right.",
    lines: 12,
  },
  {
    title: "8. Calculations",
    prompt:
      "Show every formula, substitution, and unit. Carry units through. Round at the END only.",
    lines: 10,
  },
  {
    title: "9. Graph / Diagram",
    prompt: "Title, labeled axes with units, best-fit or trend line. Attach printed graphs here.",
    lines: 10,
  },
  {
    title: "10. Analysis & Error",
    prompt:
      "What does the data mean? Compare to expected. Calculate % error. Identify random vs. systematic error.",
    lines: 6,
  },
  {
    title: "11. Conclusion",
    prompt:
      "Restate the question, answer it with evidence from your data, and state next-step questions.",
    lines: 6,
  },
] as const;

function generateNotebookPdf() {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const MARGIN = 48;
  const LINE_GAP = 18;

  function header(pageTitle: string) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text("ELEMENTPAD · LAB NOTEBOOK", MARGIN, 28);
    doc.setTextColor(37, 99, 235);
    doc.text(pageTitle.toUpperCase(), PAGE_W - MARGIN, 28, { align: "right" });
    doc.setDrawColor(220);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, 36, PAGE_W - MARGIN, 36);
  }

  function footer(pageNum: number, total: number) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Page ${pageNum} of ${total}`,
      PAGE_W - MARGIN,
      PAGE_H - 24,
      { align: "right" }
    );
    doc.text(
      "Privacy-first: this is a printable template. ElementPad does not collect anything you write here.",
      MARGIN,
      PAGE_H - 24
    );
  }

  // Cover page
  header("Cover");
  let y = 100;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.setTextColor(20);
  doc.text("Lab Notebook", MARGIN, y);
  y += 30;
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80);
  doc.text(
    "A consistent place to record every investigation: hypothesis, procedure, data, analysis, conclusion.",
    MARGIN,
    y,
    { maxWidth: PAGE_W - 2 * MARGIN }
  );
  y += 50;

  const fields = [
    "Student name",
    "Class / Period",
    "Teacher",
    "School year",
    "Lab partner(s)",
  ];
  doc.setFontSize(10);
  fields.forEach((f) => {
    doc.setTextColor(60);
    doc.text(f, MARGIN, y);
    doc.setDrawColor(180);
    doc.line(MARGIN + 110, y + 2, PAGE_W - MARGIN, y + 2);
    y += 26;
  });

  y += 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(37, 99, 235);
  doc.text("HOW TO USE THIS NOTEBOOK", MARGIN, y);
  y += 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(60);
  const instructions = [
    "• Print one section per experiment, or print the whole pack and tab it.",
    "• Always write in pen. Cross out mistakes with one line - never erase.",
    "• Record data immediately, with units and sig figs. Don't rewrite later.",
    "• Stuck? Go back to your hypothesis before changing the procedure.",
  ];
  instructions.forEach((line) => {
    doc.text(line, MARGIN, y, { maxWidth: PAGE_W - 2 * MARGIN });
    y += 16;
  });

  // Section pages
  SECTIONS.forEach((section) => {
    doc.addPage();
    header(section.title);
    let yy = 70;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(20);
    doc.text(section.title, MARGIN, yy);
    yy += 18;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(110);
    const wrapped = doc.splitTextToSize(section.prompt, PAGE_W - 2 * MARGIN);
    doc.text(wrapped, MARGIN, yy);
    yy += wrapped.length * 12 + 14;

    // Lined writing space - fill rest of page
    doc.setDrawColor(220);
    doc.setLineWidth(0.3);
    while (yy < PAGE_H - 50) {
      doc.line(MARGIN, yy, PAGE_W - MARGIN, yy);
      yy += LINE_GAP;
    }
  });

  // Number all pages
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    footer(i, total);
  }

  doc.save("elementpad-lab-notebook.pdf");
}

export default function LabNotebookDownload() {
  const [busy, setBusy] = useState(false);

  const handleDownload = () => {
    setBusy(true);
    try {
      generateNotebookPdf();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-tile-bg p-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-primary/10 text-primary p-2">
          <Notebook size={18} aria-hidden="true" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">
            Printable lab notebook
          </p>
          <p className="text-xs text-muted-foreground max-w-md">
            12 pages with prompts for hypothesis, procedure, data, error analysis,
            and conclusion. Generated on your device - nothing uploaded.
          </p>
        </div>
      </div>
      <Button onClick={handleDownload} disabled={busy} size="sm" className="gap-1.5">
        <Download size={14} aria-hidden="true" />
        {busy ? "Building PDF…" : "Download PDF"}
      </Button>
    </div>
  );
}